import React, { useState, useEffect } from "react";
import styles from "./prefactura-to-factura-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Dropdown from "../../ui/dropdown";
import Button from "../../ui/button";
import Table from "../../ui/table";
import { createFacturaFromPrefactura, getBudgetLinesForInvoice, changePresupuestoIva } from "../../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";
import { getTodayISO } from "../../../utils/dateUtils";

const PrefacturaToFacturaModal = ({ isOpen, onClose, prefacturaData, prefacturaId, onSuccess }) => {
  const [formData, setFormData] = useState({
    numero: "",
    fecha: getTodayISO(),
    tipoCobro: null,
    iva: null,
    costePresupuesto: "",
    costeFactura: "",
    porcentaje: "",
    clientType: { label: "Cliente", value: "cliente" },
    nombre: "",
    domicilio: "",
    localidad: null,
    nif: "",
  });

  const [budgetData, setBudgetData] = useState(null);
  const [loadingBudgetData, setLoadingBudgetData] = useState(false);

  const [tipoCobroOptions] = useState([
    { label: "Transferencia", value: "Transferencia" },
    { label: "Metalico", value: "Metalico" },
    { label: "Bizum", value: "Bizum" },
    { label: "Giro", value: "Giro" },
  ]);

  const [ivaOptions] = useState([
    { label: "21%", value:"21%" },
    { label: "10%", value: "10%" },
  ]);

  const [clientTypeOptions] = useState([
    { label: "Cliente", value: "cliente" },
    { label: "Compañía", value: "compania" },
  ]);

  const [localityOptions, setLocalityOptions] = useState([]);

  useEffect(() => {
    if (prefacturaData) {
      // Si iva ya es un objeto (viene de invoices con getBudgetForInvoice), usarlo directamente
      // Si es string, convertirlo a objeto
      const ivaValue = prefacturaData.iva 
        ? (typeof prefacturaData.iva === 'object' 
            ? prefacturaData.iva 
            : { label: prefacturaData.iva, value: prefacturaData.iva })
        : null;

      setFormData({
        numero: prefacturaData.numero || "",
        fecha: prefacturaData.fecha || getTodayISO(),
        tipoCobro: prefacturaData.tipoCobro || null,
        iva: ivaValue,
        costePresupuesto: prefacturaData.costePresupuesto || "",
        costeFactura: prefacturaData.costeFactura || "",
        porcentaje: prefacturaData.porcentaje || "",
        clientType: prefacturaData.clientType || { label: "Cliente", value: "cliente" },
        nombre: prefacturaData.nombre || "",
        domicilio: prefacturaData.domicilio || "",
        localidad: prefacturaData.localidad || null,
        nif: prefacturaData.nif || "",
      });
    }
  }, [prefacturaData]);

  useEffect(() => {
    // Cargar las líneas de presupuesto cuando se abra el modal
    const fetchBudgetLines = async () => {
      if (!prefacturaData?.presupuestoId) return;
      
      try {
        setLoadingBudgetData(true);
        const data = await getBudgetLinesForInvoice(prefacturaData.presupuestoId);
        setBudgetData(data);
      } catch (error) {
        console.error("Error fetching budget lines:", error);
      } finally {
        setLoadingBudgetData(false);
      }
    };

    if (isOpen && prefacturaData) {
      fetchBudgetLines();
    }
  }, [isOpen, prefacturaData]);

  const handleInputChange = (field, value) => {
    const stringValue = typeof value === 'string' ? value : String(value || '');
    setFormData(prev => ({
      ...prev,
      [field]: stringValue
    }));
  };

  const handleDropdownChange = (field, selectedOption) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [field]: selectedOption
      };

      // Si se cambia el tipo de cliente/compañía, actualizar los campos automáticamente
      if (field === "clientType" && budgetData) {
        if (selectedOption.value === "cliente") {
          newFormData.nombre = budgetData.nombreCliente || "";
          newFormData.domicilio = budgetData.domcilioCliente || "";
          newFormData.localidad = budgetData.localidadCliente ? {
            label: `${budgetData.localidadCliente.municipio} (${budgetData.localidadCliente.codigoPostal})`,
            value: budgetData.localidadCliente.id
          } : null;
          newFormData.nif = budgetData.nifCliente || "";
        } else if (selectedOption.value === "compania") {
          newFormData.nombre = budgetData.nombreCompania || "";
          newFormData.domicilio = budgetData.domcilioCompania || "";
          newFormData.localidad = budgetData.localidadCompania ? {
            label: `${budgetData.localidadCompania.municipio} (${budgetData.localidadCompania.codigoPostal})`,
            value: budgetData.localidadCompania.id
          } : null;
          newFormData.nif = budgetData.nifCompania || "";
        }
      }

      return newFormData;
    });
  };

  const isFormValid = () => {
    return formData.tipoCobro && 
           formData.iva !== null && 
           formData.costeFactura && 
           formData.clientType &&
           formData.nombre && 
           formData.domicilio && 
           formData.localidad && 
           formData.nif;
  };

  const handleCreate = async () => {
    try {
      // Si el IVA ha cambiado, actualizar primero en el presupuesto
      if (prefacturaData?.presupuestoId && formData.iva && budgetData?.iva !== formData.iva.value) {
        try {
          await changePresupuestoIva(prefacturaData.presupuestoId, formData.iva.value);
        } catch (error) {
          console.error("Error al cambiar el IVA:", error);
          showErrorToast("Error al actualizar el IVA");
          return;
        }
      }

      // LOG: mostrar qué id de prefactura se va a usar para crear la factura
      try {
        console.log("[LOG] Crear factura desde prefactura - enviando petición:", { endpoint: `/factura/crearByPrefactura/${prefacturaId}`, prefacturaId, prefacturaData });
      } catch (logErr) {
        console.log("[LOG] Crear factura desde prefactura - prefacturaId:", prefacturaId);
      }

      // Solo necesitamos el ID, la API crea la factura basándose en la pre-factura
      await createFacturaFromPrefactura(prefacturaId);
      
      showSuccessToast("Factura creada desde pre-factura exitosamente");
      
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Error creating factura:", error);
      const apiMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
      showErrorToast(apiMessage || "Error al crear factura");
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Crear factura desde pre-factura</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons name="close" size={24} color="black" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.section}>
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Núm. presupuesto</label>
                <Input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => handleInputChange("numero", e.target.value)}
                  placeholder="5049"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Fecha</label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleInputChange("fecha", e.target.value)}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Tipo de cobro</label>
                <Dropdown
                  options={tipoCobroOptions}
                  selected={formData.tipoCobro}
                  onSelect={(option) => handleDropdownChange("tipoCobro", option)}
                  placeholder="Seleccionar"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>IVA</label>
                <Dropdown
                  options={ivaOptions}
                  selected={formData.iva}
                  onSelect={(option) => handleDropdownChange("iva", option)}
                  placeholder="Seleccionar"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Total presupuesto</label>
                <Input
                  type="text"
                  value={formData.costePresupuesto}
                  onChange={(e) => handleInputChange("costePresupuesto", e.target.value)}
                  placeholder="Precio"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Total factura</label>
                <Input
                  type="text"
                  value={formData.costeFactura}
                  onChange={(e) => handleInputChange("costeFactura", e.target.value)}
                  placeholder="Precio"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Porcentaje</label>
                <Input
                  type="text"
                  value={formData.porcentaje}
                  onChange={(e) => handleInputChange("porcentaje", e.target.value)}
                  placeholder="%"
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.clientHeader}>
              <Dropdown
                options={clientTypeOptions}
                selected={formData.clientType}
                onSelect={(option) => handleDropdownChange("clientType", option)}
                clearable={false}
              />
            </div>
            
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Nombre</label>
                <Input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange("nombre", e.target.value)}
                  placeholder="Nombre"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Domicilio</label>
                <Input
                  type="text"
                  value={formData.domicilio}
                  onChange={(e) => handleInputChange("domicilio", e.target.value)}
                  placeholder="Domicilio"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Localidad</label>
                <Dropdown
                  options={localityOptions}
                  selected={formData.localidad}
                  onSelect={(option) => handleDropdownChange("localidad", option)}
                  placeholder="Seleccionar localidad"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>NIF</label>
                <Input
                  type="text"
                  value={formData.nif}
                  onChange={(e) => handleInputChange("nif", e.target.value)}
                  placeholder="Número NIF"
                />
              </div>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>Líneas de Presupuesto</h3>
          {loadingBudgetData ? (
            <div className={styles.loadingContainer}>
              <p>Cargando líneas de presupuesto...</p>
            </div>
          ) : budgetData?.lineas && budgetData.lineas.length > 0 ? (
            <Table
              headers={[
                { key: "gremio", label: "Gremio" },
                { key: "presupuestoTotal", label: "Presupuesto Total" },
                { key: "costeTotal", label: "Coste Total" },
                { key: "conjuntoTotal", label: "R. Conjunto" },
                { key: "beneficioTotal", label: "Beneficio" },
                { key: "realizado", label: "Realizado" },
                { key: "noRealizar", label: "No Realizar" },
              ]}
              data={budgetData.lineas.map((line, index) => ({
                ...line,
                id: index,
                presupuestoTotal: `${line.presupuestoTotal}€`,
                costeTotal: `${line.costeTotal}€`,
                conjuntoTotal: `${(line.conjuntoTotal * 100).toFixed(2)}%`,
                beneficioTotal: `${line.beneficioTotal}€`,
                realizado: line.realizado ? "Sí" : "No",
                noRealizar: line.noRealizar ? "Sí" : "No",
              }))}
              renderActions={(row) => (
                <button
                  className={styles.editButton}
                  onClick={() => {
                    console.log("Editar línea:", row);
                  }}
                  title="Editar línea"
                >
                  <MyIcons name="settings" width={20} height={20} color={"var(--main)"} />
                </button>
              )}
            />
          ) : (
            <div className={styles.emptyState}>
              <p>No hay líneas de presupuesto disponibles</p>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!isFormValid()}
            className={styles.createButton}
          >
            Crear Factura
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrefacturaToFacturaModal;
